import { useCallback, useEffect, useRef, useState } from "react";
import { extForMime, pickMimeType } from "@/hooks/useRecorder";

export function useCaptureRecorder() {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [mimeType] = useState(pickMimeType);
  const rec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const acc = useRef(0);
  const since = useRef(0);
  const lock = useRef(null);
  const resolver = useRef(null);

  const tick = () => setElapsed(acc.current + (since.current ? Date.now() - since.current : 0));
  const cleanup = () => {
    clearInterval(timer.current);
    rec.current?.stream?.getTracks().forEach((t) => t.stop());
    lock.current?.release?.().catch(() => {});
    lock.current = null;
  };
  useEffect(() => cleanup, []);

  const start = useCallback(async () => {
    setError(null);
    if (mimeType === null) return setError("Este navegador no soporta grabación de audio.");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = () => {
        cleanup();
        const type = r.mimeType || mimeType || "audio/webm";
        setState("idle");
        resolver.current?.({ blob: new Blob(chunks.current, { type }), ext: extForMime(type) });
      };
      rec.current = r;
      r.start(1000);
      acc.current = 0;
      since.current = Date.now();
      setElapsed(0);
      timer.current = setInterval(tick, 250);
      lock.current = await navigator.wakeLock?.request("screen").catch(() => null);
      setState("recording");
    } catch (e) {
      setError(e.name === "NotAllowedError" ? "Permiso de micrófono denegado. Actívalo en la configuración del navegador." : `No se pudo grabar: ${e.message}`);
      setState("idle");
    }
  }, [mimeType]);

  const pause = useCallback(() => {
    if (rec.current?.state !== "recording") return;
    rec.current.pause();
    acc.current += Date.now() - since.current;
    since.current = 0;
    setState("paused");
  }, []);

  const resume = useCallback(() => {
    if (rec.current?.state !== "paused") return;
    rec.current.resume();
    since.current = Date.now();
    setState("recording");
  }, []);

  const stop = useCallback(() => new Promise((resolve) => {
    resolver.current = resolve;
    if (rec.current && rec.current.state !== "inactive") rec.current.stop();
    else resolve(null);
  }), []);

  const cancel = useCallback(async () => { resolver.current = null; if (rec.current && rec.current.state !== "inactive") rec.current.stop(); setElapsed(0); }, []);

  return { state, elapsed, error, mimeType, start, pause, resume, stop, cancel };
}
